import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, BRAND } from "@/lib/content";

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="top-nav"
      className={`sticky top-0 z-50 border-b transition-shadow duration-200 ${
        scrolled ? "shadow-[var(--wt-shadow-nav)]" : "shadow-none"
      }`}
      style={{
        backgroundColor: "rgba(250, 247, 240, 0.96)",
        backdropFilter: "saturate(140%) blur(8px)",
        borderColor: "var(--wt-border)",
      }}
    >
      <div className="wt-container">
        <div className="h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="brand-link"
            className="flex items-center gap-3 group"
            aria-label={`${BRAND.name} home`}
          >
            <span
              aria-hidden="true"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md font-serif text-base"
              style={{
                backgroundColor: "var(--wt-primary)",
                color: "var(--wt-gold)",
                boxShadow: "inset 0 -2px 0 var(--wt-gold)",
              }}
            >
              W
            </span>
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="font-serif text-[15px] font-semibold tracking-tight wt-primary">
                {BRAND.name}
              </span>
              <span className="text-[10px] uppercase tracking-[0.18em] wt-text-muted">
                School Climate &amp; Community Partnerships
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-7" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[13px] font-medium tracking-wide transition-colors duration-150 ${
                    isActive ? "wt-primary" : "wt-text-muted hover:wt-primary"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/book-consultation"
              data-testid="nav-cta-button"
              className="wt-btn-primary text-sm"
            >
              Book a Consultation
            </Link>
          </nav>

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border"
            style={{ borderColor: "var(--wt-border)", color: "var(--wt-primary)" }}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-nav-panel"
          className="md:hidden border-t"
          style={{ borderColor: "var(--wt-border)", backgroundColor: "var(--wt-bg)" }}
        >
          <nav className="wt-container py-4 flex flex-col gap-1" aria-label="Mobile primary">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block py-2 text-base ${isActive ? "wt-primary font-semibold" : "wt-text"}`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/book-consultation"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-cta-button"
              className="wt-btn-primary mt-3 justify-center"
            >
              Book a Consultation
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
